/**
 * Two claims the field detail makes about thin catalog data (CZNA-446).
 *
 * <p>Its own module for the same reason as {@link semanticCatalogCoverage}: both are
 * claims about the **artifact**, not about the screen, so both deserve a test against
 * the real shipped `semantic-catalog.json` rather than a fixture. A fixture proves the
 * branch works; the artifact proves the sentence on the screen is true of our data.
 *
 * <p><b>Why a field has no subfields.</b> The old single message said "a control field
 * OR Aleph-internal plumbing" — one sentence for two unrelated causes, which left the
 * reader to guess which one they were looking at. `LDR` and `00X` are standard MARC 21
 * control fields: they carry one continuous string by design and it would be wrong to
 * call them Aleph's. `FMT`, `R00`, `UPD` and friends are Aleph's own record-processing
 * fields and are not MARC at all. A numeric data field (≥ 010) with no subfields is
 * neither — that would be a hole in the catalog, so it gets its own honest wording
 * instead of being silently filed under Aleph.
 *
 * <p><b>Placeholder subfield names.</b> 422 of the 583 subfields in the catalog are
 * named `Podpole $a` — the generator's stand-in for a name nobody has written yet, not
 * a name. Printing it verbatim tells the reader the subfield IS called that (this is
 * exactly what happened on `010$a` during Kontrola). The detail therefore says the
 * name is missing. This module only DETECTS the placeholder; filling the 422 real
 * names in is a catalog job, not a screen job.
 */
/** `LDR` plus `001`–`009`: MARC 21 control fields, which have no subfields by design. */
var CONTROL_FIELD = /^(LDR|00\d)$/;
/** A MARC 21 data field tag. Aleph's own fields (`FMT`, `R00`, `UPD`, …) never match. */
var MARC_DATA_FIELD = /^\d{3}$/;
export var noSubfieldsReason = function (tag) {
    if (CONTROL_FIELD.test(tag))
        return 'controlField';
    // A MARC data field with no subfields is a gap in the catalog, not Aleph plumbing.
    if (MARC_DATA_FIELD.test(tag))
        return 'unknown';
    return 'alephInternal';
};
/**
 * True when a subfield's name is the generator's placeholder rather than a name.
 *
 * <p>Matched against the subfield's OWN code, so it cannot swallow a real name that
 * merely starts with the same word; in the shipped catalog all 422 placeholders match
 * their own code exactly and nothing else is `Podpole $…`-shaped, which the artifact
 * test asserts.
 */
export var isPlaceholderSubfieldLabel = function (label, code) { return (label === "Podpole $".concat(code)); };
